import { getAdmissionOffer, getCohort, getReapplicationDate } from 'Users';
import {
    getRegistrationDeadline,
    inEarlyAdmissionsRegistrationPeriod,
    showFastTrackRegistration,
} from 'AdmissionOffer';
import * as dateHelper from 'DateHelpers';
import { afterRegistrationOpenDate, getStartDate, registrationDeadlineHasPassed, registrationOpenDate } from 'Cohorts';
import { getRegisterButton, getRegisterButtonState } from './getRegisterButtonConfig';
import { getPaymentConfigState } from './getPaymentConfig';
import { RegisterButtonState } from './TuitionAndRegistration.types';

function openStripeCheckoutSession(scope, $injector, params) {
    const checkoutSessionsEndpoint = `${window.ENDPOINT_ROOT}/api/stripe_checkout_sessions`;

    scope.processingRegistration = true;

    // skip:true so we can intercept the already-registered 409 ourselves; everything else
    // falls back to the global handler. We use .then(success, error) rather than async/await
    // so $q sees a rejection handler attached synchronously and doesn't flag "possibly
    // unhandled rejection" before our handler runs.
    return $injector
        .get('$http')
        .post(checkoutSessionsEndpoint, params, { 'FrontRoyal.ApiErrorHandler': { skip: true } })
        .then(onCheckoutSessionCreated, onCheckoutSessionCreationFailed);

    function onCheckoutSessionCreated(response) {
        const session = response?.data?.session;

        // TODO: If we have a session but no URL here, it probably means getStripeCheckoutSessionUrl
        // returned a completed checkout session — e.g. user had two browser windows open and
        // registered in the other. We just do nothing in that case; consider handling explicitly?
        if (session?.url) {
            $injector.get('$window').location.href = session.url;
        } else {
            scope.processingRegistration = false;
        }
    }

    function onCheckoutSessionCreationFailed(response) {
        // 409 from this endpoint means we already have an active subscription for this
        // admission offer — the user completed checkout but the client hadn't picked up
        // the new state yet. Force a status ping so currentUser syncs from the server
        // (push_messages.current_user is merged in by CurrentUserInterceptor).
        if (response?.status === 409) {
            $injector.get('HttpQueue').unfreezeAfterError(response.config);
            return $injector
                .get('FrontRoyal.StatusPoller')
                .send()
                .finally(() => {
                    scope.processingRegistration = false;
                });
        }

        // Anything else: defer to the global error handler, same UX as before.
        response.config['FrontRoyal.ApiErrorHandler'].skip = false;
        scope.processingRegistration = false;
        return $injector.get('FrontRoyal.ApiErrorHandler').onResponseError(response);
    }
}

// We don't allow registration on Cordova - we should redirect them to a system browser
// so they can initiate a checkout session from there.
function openApplicationStatusInBrowser(_scope, $injector) {
    const applicationStatusTarget = `${$injector.get('$window').ENDPOINT_ROOT}/settings/application_status`;
    $injector.get('Navigation.NavigationHelperMixin').loadUrl(applicationStatusTarget, '_blank');
}

export function attachOpenStripeCheckoutSessionFn(scope, $injector) {
    // We don't allow registration on Cordova.
    if ($injector.get('$window').CORDOVA) {
        // This is used in the cohortRegistrationPlanSelection and cohortRegistrationNoPlanSelection templates,
        // so we need to add it to the scope.
        scope.registerRequiresRedirectToBrowser = true;
        scope.openStripeCheckoutSessionOrBrowser = openApplicationStatusInBrowser;
    } else {
        scope.openStripeCheckoutSessionOrBrowser = openStripeCheckoutSession;
    }
}

export function attachDeferralModalFn(scope, $injector, scholarship) {
    const TranslationHelper = $injector.get('TranslationHelper');
    const DialogModal = $injector.get('DialogModal');
    const translationHelper = new TranslationHelper('settings.tuition_and_registration');

    scope.openDeferralModal = () => {
        const p1Key = scholarship ? 'preserve_your_admission_and_scholarship' : 'preserve_your_admission';
        const p1Text = translationHelper.get(p1Key);

        const p2Text = translationHelper.get('email_us_to_request_future_start_date', {
            admissionsEmail: scope.currentUser.relevantCohort.supportEmailAddress('admissions'),
        });

        const currentReapplicationDate = getReapplicationDate(scope.currentUser);
        const reapplicationMonthYear = currentReapplicationDate
            ? dateHelper.formattedUserFacingMonthYearLong(currentReapplicationDate, false)
            : undefined;
        const p3Key = reapplicationMonthYear ? 'reapply_at_different_time' : 'reapply_at_different_time_no_date';
        const p3Text = translationHelper.get(p3Key, {
            reapplicationMonthYear,
        });
        const showP3 = !currentReapplicationDate || (currentReapplicationDate && currentReapplicationDate > new Date());

        const p4Text = translationHelper.get('refund_policy');

        DialogModal.alert({
            title: translationHelper.get('deferral_modal_title'),
            content: `
                <p>${p1Text}</p>
                <p>${p2Text}</p>
                ${showP3 ? `<p>${p3Text}</p>` : ''}
                <p>${p4Text}</p>
            `,
            scope: {
                loadFaqUrl: $injector.get('Navigation.NavigationHelperMixin').loadFaqUrl,
            },
        });
    };
}

export function attachRegisterButtonWatchFn(scope, defaultState = RegisterButtonState.review_and_register) {
    scope.$watchGroup(
        [
            'processingRegistration',
            'registerRequiresRedirectToBrowser',
            'paymentConfig',
            'activePaymentView',
            'selectedPaymentTypeProxy.value',
            'additionalFinancingOptionIsSelected',
        ],
        ([
            processingRegistration,
            registerRequiresRedirectToBrowser,
            paymentConfig,
            activePaymentView,
            selectedPaymentType,
            additionalFinancingOptionIsSelected,
        ]) => {
            const registerButtonState = getRegisterButtonState({
                processingRegistration,
                registerRequiresRedirectToBrowser,
                paymentConfig,
                activePaymentView,
                selectedPaymentType,
                defaultState,
                additionalFinancingOptionIsSelected,
            });

            scope.registerButton = getRegisterButton({ registerButtonState });
        },
    );
}

export function attachRegisterEnabledWatchFn(scope) {
    scope.$watchGroup(
        [() => afterRegistrationOpenDate(getCohort(scope.currentUser)), 'inEarlyAdmissionsRegistrationPeriod'],
        ([afterRegistrationOpenDateForCohort, inEarlyAdmissionsRegistrationPeriodOnScope]) => {
            scope.registerEnabled = afterRegistrationOpenDateForCohort || inEarlyAdmissionsRegistrationPeriodOnScope;
        },
    );
}

export function attachPaymentConfigStateWatchFn(scope) {
    scope.$watchGroup(
        ['currentUser.country', 'currentUser.shouldChargeCreditCardPaymentFee'],
        ([country, shouldChargeCreditCardPaymentFee]) => {
            if (!scope.currentUser) return;

            scope.paymentConfigState = getPaymentConfigState({
                country,
                shouldChargeCreditCardPaymentFee,
            });
        },
    );
}

export function attachCohortDatesFn(scope) {
    const admissionOffer = getAdmissionOffer(scope.currentUser);
    const cohort = getCohort(scope.currentUser);
    const registrationDeadline = getRegistrationDeadline(admissionOffer, cohort);

    scope.beforeRegistrationDeadline = !registrationDeadlineHasPassed(cohort) && !afterRegistrationOpenDate(cohort);
    scope.classStartDate = dateHelper.formattedUserFacingMonthDayYearLong(getStartDate(cohort), false);
    scope.classStartDateMobile = dateHelper.formattedUserFacingMonthDayLong(getStartDate(cohort), false);
    scope.inEarlyAdmissionsRegistrationPeriod = inEarlyAdmissionsRegistrationPeriod(admissionOffer);
    scope.registrationDeadline = dateHelper.formattedUserFacingMonthDayYearLong(registrationDeadline);
    scope.registrationDeadlineMobile = dateHelper.formattedUserFacingMonthDayLong(registrationDeadline);
    scope.registrationOpenDate = dateHelper.formattedUserFacingMonthDayYearLong(registrationOpenDate(cohort), false);

    const currentReapplicationDate = getReapplicationDate(scope.currentUser);
    if (currentReapplicationDate) {
        scope.reapplicationDate = dateHelper.formattedUserFacingMonthYearLong(currentReapplicationDate, false);
    }
}

// Offers to programs that supports_registering_into_other_cohorts_on_offer? see multiple Cohort options on the registration screen.
// When the user has a "fast track" offer, they see: fast track Cohort, offer Cohort, the next upcoming Cohort
// When the user has a normal offer, they see: offer Cohort and the next two upcoming Cohorts.
export function attachCohortRegistrationOptionsFn(scope) {
    const admissionOffer = getAdmissionOffer(scope.currentUser);

    scope.showFastTrackRegistration = showFastTrackRegistration(admissionOffer);

    // We always show the offer Cohort.
    let cohortRegistrationOptions = [{ cohortId: admissionOffer.cohortId, date: scope.classStartDate }];

    // If the user should see the fast track option to register into the prior Cohort, it should be first.
    // This will not be populated if the Cohort doesn't supports_registering_into_other_cohorts_on_offer?.
    if (scope.showFastTrackRegistration) {
        const fastTrackStartDateTs = getAdmissionOffer(scope.currentUser)?.fastTrackStartDate;
        const fastTrackStartDate = fastTrackStartDateTs
            ? dateHelper.formattedUserFacingMonthDayYearLong(1000 * fastTrackStartDateTs, false)
            : null;
        cohortRegistrationOptions.push({ cohortId: admissionOffer.fastTrackCohortId, date: fastTrackStartDate });
    }

    // Upcoming Cohorts should always come after the offer Cohort.
    // This will not be populated if the Cohort doesn't supports_registering_into_other_cohorts_on_offer?.
    const upcomingCohortDates =
        admissionOffer?.upcomingCohortDates?.map(cohortAttrs => ({
            cohortId: cohortAttrs.id,
            date: dateHelper.formattedUserFacingMonthDayYearLong(new Date(cohortAttrs.startDate * 1000), false),
        })) || [];
    cohortRegistrationOptions.push(...upcomingCohortDates);

    cohortRegistrationOptions = cohortRegistrationOptions
        // Sort options by start date ascending
        .sort((a, b) => (Date.parse(a.date) < Date.parse(b.date) ? -1 : 1))
        // Cap the options to a maximum of three
        .slice(0, 3);

    scope.cohortRegistrationOptions = cohortRegistrationOptions;

    // Every AdmissionOffer to all programs will have at least one entry here, for the offer Cohort.
    // If the program supports_registering_into_other_cohorts_on_offer?, there will be other entries to show.
    scope.showMultipleCohortRegistrationOptions = scope.cohortRegistrationOptions.length > 1;
}
